export const DOCK_MARGIN = 16
export const BUBBLE_SIZE = 58
export const TAB_HEIGHT = 46
export const HIDE_TRAVEL = 120
export const TAB_TRAVEL = 30
export const TAP_SLOP = 4
export const HIDE_THRESHOLD = 54
export const RUBBER_BAND_EXPONENT = 0.7
export const KEYBOARD_STEP = 24
export const PANEL_HEIGHT = 620
export const PANEL_GAP = 16
export const PANEL_TOP_MARGIN = 32
export const PANEL_BOTTOM_MARGIN = 32
export const MIN_PANEL_HEIGHT = 320
export const MIN_PANEL_MAX_HEIGHT = 160

const usableViewport = height => Number.isFinite(height) && height > 0
// Number(null) and Number('') are 0, which would read as a position at the very
// top of the track rather than as the absence of one.
const toNumber = value => (value === null || value === '' ? Number.NaN : Number(value))

export function maxDockTop({ viewportHeight, height = BUBBLE_SIZE, margin = DOCK_MARGIN } = {}) {
  if (!usableViewport(viewportHeight)) return Number.POSITIVE_INFINITY
  return Math.max(margin, viewportHeight - height - margin)
}

// Nothing stored means the bottom of the track: the launcher has always sat in the
// bottom right corner, and a merchant who never drags it must not see it move.
export function defaultDockTop({ viewportHeight, height = BUBBLE_SIZE, margin = DOCK_MARGIN } = {}) {
  const highest = maxDockTop({ viewportHeight, height, margin })
  return Number.isFinite(highest) ? highest : margin
}

export function clampDockTop({ top, viewportHeight, height = BUBBLE_SIZE, margin = DOCK_MARGIN } = {}) {
  const wanted = toNumber(top)
  if (!Number.isFinite(wanted)) return defaultDockTop({ viewportHeight, height, margin })
  return Math.round(Math.min(Math.max(wanted, margin), maxDockTop({ viewportHeight, height, margin })))
}

export function isDockTopRatio(ratio) {
  return Number.isFinite(ratio) && ratio >= 0 && ratio <= 1
}

export function dockTopRatio({ top, viewportHeight } = {}) {
  const value = toNumber(top)
  if (!usableViewport(viewportHeight) || !Number.isFinite(value)) return null
  return Math.min(Math.max(value / viewportHeight, 0), 1)
}

// A fraction of the viewport rather than a pixel offset, so a resize or a rotate
// lands the launcher at the same place on the new screen instead of off it.
export function dockTopFromRatio({ ratio, viewportHeight, height = BUBBLE_SIZE, margin = DOCK_MARGIN } = {}) {
  const value = toNumber(ratio)
  if (!isDockTopRatio(value) || !usableViewport(viewportHeight)) {
    return defaultDockTop({ viewportHeight, height, margin })
  }
  return clampDockTop({ top: value * viewportHeight, viewportHeight, height, margin })
}

export function dockTopFromDrag({
  startTop,
  startY,
  currentY,
  viewportHeight,
  height = BUBBLE_SIZE,
  margin = DOCK_MARGIN
} = {}) {
  const delta = Number(currentY) - Number(startY)
  const moved = Number.isFinite(delta) ? Number(startTop) + delta : Number(startTop)
  return clampDockTop({ top: moved, viewportHeight, height, margin })
}

// Rightward only - there is nothing to the left of the assembly - and resisting
// past the threshold, so the edge has a detent rather than a cliff.
export function rubberBandPull({ dx, threshold = HIDE_THRESHOLD, exponent = RUBBER_BAND_EXPONENT } = {}) {
  const value = Number(dx)
  if (!Number.isFinite(value) || value <= 0) return 0
  if (value <= threshold) return value
  return threshold + Math.pow(value - threshold, exponent)
}

export function hideArmed({ dx, threshold = HIDE_THRESHOLD } = {}) {
  const value = Number(dx)
  return Number.isFinite(value) && value > threshold
}

export function dragExceededThreshold({ startX = 0, startY, currentX = 0, currentY, threshold = TAP_SLOP } = {}) {
  const dx = Number(currentX) - Number(startX)
  const dy = Number(currentY) - Number(startY)
  if (!Number.isFinite(dx) || !Number.isFinite(dy)) return false
  return Math.abs(dx) + Math.abs(dy) > threshold
}

export function tabTop({
  dockTop,
  viewportHeight,
  height = TAB_HEIGHT,
  dockHeight = BUBBLE_SIZE,
  margin = DOCK_MARGIN
} = {}) {
  return clampDockTop({ top: Number(dockTop) + (dockHeight - height) / 2, viewportHeight, height, margin })
}

export function panelAnchor({
  dockTop,
  viewportHeight,
  panelHeight = PANEL_HEIGHT,
  dockHeight = BUBBLE_SIZE,
  gap = PANEL_GAP,
  topMargin = PANEL_TOP_MARGIN,
  bottomMargin = PANEL_BOTTOM_MARGIN,
  minPanelHeight = MIN_PANEL_HEIGHT
} = {}) {
  const height = usableViewport(viewportHeight) ? viewportHeight : panelHeight + topMargin + dockHeight + gap
  const anchor = clampDockTop({ top: dockTop, viewportHeight: height, height: dockHeight })

  const above = height - anchor + gap
  const below = anchor + dockHeight + gap
  const roomAbove = anchor - gap - topMargin
  const roomBelow = height - below - bottomMargin

  const flip = roomAbove < minPanelHeight && (roomBelow >= minPanelHeight || roomBelow > roomAbove)
  const offset = flip ? below : above
  const room = flip ? roomBelow : roomAbove

  // BotMessageList scrolls inside an absolute inset-0 box, so a non-positive
  // max-height collapses the whole thread to nothing rather than just cropping it.
  return {
    placement: flip ? 'below' : 'above',
    offset,
    maxHeight: Math.max(Math.min(room, panelHeight), MIN_PANEL_MAX_HEIGHT)
  }
}
